import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { ID, AppAspect, AppRelationship, TaxonomyData, AppCitation, AppTheme } from './types';
import { useTaxonomyStore } from './taxonomyStore';
import MindMapColumn from './MindMapColumn';
import Portal from './Portal';
import DetailsDrawer from './DetailsDrawer';

interface TaxonomyExplorerProps {
    taxonomyData: TaxonomyData;
    taxonomyIdentifier: string;
}

const getChildAspects = (parentId: ID | undefined, targetLevel: string, allAspects: AppAspect[], relationships: AppRelationship[]): AppAspect[] => {
    if (!parentId) return [];
    return relationships
        .filter(r => r.parent_id === parentId && r.type === 'aspect_to_aspect')
        .map(r => allAspects.find(a => a.name === r.child_id && a.level === targetLevel))
        .filter(Boolean) as AppAspect[];
};

const getThemesForAspect = (aspectId: ID | undefined, allThemes: AppTheme[]): AppTheme[] => {
    if (!aspectId) return [];
    return allThemes.filter(theme =>
        theme.parentAspectName === aspectId || theme.parent_aspect_name === aspectId
    );
};

const TaxonomyExplorer: React.FC<TaxonomyExplorerProps> = ({ taxonomyData, taxonomyIdentifier }) => {
    const path = useTaxonomyStore(state => state.path);
    const focusedItem = useTaxonomyStore(state => state.focusedItem);
    const expandedAspectThemeLists = useTaxonomyStore(state => state.expandedAspectThemeLists);
    const { setL1, setL2, setL3, setTheme, toggleAspectThemeList, setFocusedItem } = useTaxonomyStore();

    const { nodes: allAspects, themes: allThemes = [], relationships, org_slug } = taxonomyData;

    // Ref for the scrollable container
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const breadcrumbRef = useRef<HTMLDivElement>(null);

    // Fixed column width - scaled to 75% of original
    const COLUMN_WIDTH = 336; // 448 * 0.75 = 336

    // Sync breadcrumb scroll with main content
    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current && breadcrumbRef.current) {
            breadcrumbRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
        }
    }, []);

    const globalFeedbackIdToNumberMap = useMemo(() => {
        const map = new Map<string, number>();
        const uniqueFeedbackIds = new Set<string>();

        allThemes.forEach(theme => {
            theme.citations?.forEach((citation: AppCitation) => uniqueFeedbackIds.add(citation.feedback_id));
            theme.sub_themes?.forEach(subTheme => {
                subTheme.citations?.forEach((citation: AppCitation) => uniqueFeedbackIds.add(citation.feedback_id));
            });
        });

        Array.from(uniqueFeedbackIds).forEach((id, index) => {
            map.set(id, index + 1); // 1-based numbering
        });
        return map;
    }, [allThemes]);

    // Get aspects for each level
    const l1Items = React.useMemo(() => allAspects.filter(a => a.level === 'L1'), [allAspects]);
    const l2Items = React.useMemo(() => path.l1 ? getChildAspects(path.l1, 'L2', allAspects, relationships) : [], [path.l1, allAspects, relationships]);
    const l3Items = React.useMemo(() => path.l2 ? getChildAspects(path.l2, 'L3', allAspects, relationships) : [], [path.l2, allAspects, relationships]);

    // Get themes for each level
    const l1Themes = React.useMemo(() => path.l1 ? getThemesForAspect(path.l1, allThemes) : [], [path.l1, allThemes]);
    const l2Themes = React.useMemo(() => path.l2 ? getThemesForAspect(path.l2, allThemes) : [], [path.l2, allThemes]);
    const l3Themes = React.useMemo(() => path.l3 ? getThemesForAspect(path.l3, allThemes) : [], [path.l3, allThemes]);

    const handleSelectAspect = useCallback((aspectName: ID, level: number) => {
        if (level === 1) {
            if (path.l1 === aspectName) { // Deselecting
                setL1(undefined);
            } else { // Selecting
                setL1(aspectName);
                // Scroll to show column 2 with some offset to make it more centered
                setTimeout(() => {
                    if (scrollContainerRef.current && breadcrumbRef.current) {
                        const scrollTarget = Math.max(0, COLUMN_WIDTH - 100); // Offset to show more of column 2
                        scrollContainerRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                        breadcrumbRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        } else if (level === 2 && path.l1) {
            if (path.l2 === aspectName) { // Deselecting
                setL2(undefined);
            } else { // Selecting
                setL2(aspectName);
                // Scroll to show column 3 with better centering
                setTimeout(() => {
                    if (scrollContainerRef.current && breadcrumbRef.current) {
                        const scrollTarget = Math.max(0, (COLUMN_WIDTH * 2) - 100);
                        scrollContainerRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                        breadcrumbRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        } else if (level === 3 && path.l1 && path.l2) {
            if (path.l3 === aspectName) { // Deselecting
                setL3(undefined);
            } else { // Selecting
                setL3(aspectName);
                // Always scroll to show column 4 area when selecting L3
                setTimeout(() => {
                    if (scrollContainerRef.current && breadcrumbRef.current) {
                        const scrollTarget = Math.max(0, (COLUMN_WIDTH * 3) - 100);
                        scrollContainerRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                        breadcrumbRef.current.scrollTo({
                            left: scrollTarget,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        }
    }, [path, setL1, setL2, setL3, COLUMN_WIDTH]);

    const handleSelectTheme = useCallback((themeName: string) => {
        setTheme(themeName);
    }, [setTheme]);

    useEffect(() => {
        if (path.l1 && !path.l2 && l2Items.length > 0 && focusedItem?.level === 1 && focusedItem.aspectName === path.l1) {
            setFocusedItem({ level: 2, aspectName: l2Items[0].name });
        }
        else if (path.l1 && path.l2 && !path.l3 && l3Items.length > 0 && focusedItem?.level === 2 && focusedItem.aspectName === path.l2) {
            setFocusedItem({ level: 3, aspectName: l3Items[0].name });
        }
    }, [path.l1, path.l2, path.l3, l2Items, l3Items, focusedItem, setFocusedItem]);

    const selectedTheme = allThemes.find(theme => theme.name === path.theme);

    const handleBreadcrumbClick = useCallback((aspectName: string, level: number) => {
        if (level === 1) {
            setL1(aspectName);
        } else if (level === 2) {
            setL2(aspectName);
        } else if (level === 3) {
            setL3(aspectName);
        }
    }, [setL1, setL2, setL3]);

    return (
        <div className="flex flex-col h-full bg-gray-50/30 dark:bg-gray-900/30">
            {/* Aligned breadcrumb that follows column structure */}
            <div className="flex bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden scrollbar-hide shadow-sm" ref={breadcrumbRef}>
                {/* L1 breadcrumb - shows org name */}
                <div className="flex-shrink-0 px-4 py-2.5 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900" style={{ width: COLUMN_WIDTH }}>
                    <div className="flex items-center justify-center h-full">
                        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">{org_slug || 'Organization'}</span>
                    </div>
                </div>

                {/* L2 breadcrumb - shows L1 selection with L1 label */}
                <div className="flex-shrink-0 px-4 py-2.5 border-r border-gray-200 dark:border-gray-700 flex items-center justify-between" style={{ width: COLUMN_WIDTH }}>
                    {path.l1 ? (
                        <div className="flex items-center gap-2 flex-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">L1</span>
                            <button
                                onClick={() => handleBreadcrumbClick(path.l1!, 1)}
                                className="text-xs font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left flex-1"
                            >
                                {path.l1}
                            </button>
                        </div>
                    ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Select L1 aspect</span>
                    )}
                    {path.l1 && path.l2 && (
                        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </div>

                {/* L3 breadcrumb - shows L2 selection with L2 label */}
                <div className="flex-shrink-0 px-4 py-2.5 border-r border-gray-200 dark:border-gray-700 flex items-center justify-between" style={{ width: COLUMN_WIDTH }}>
                    {path.l2 ? (
                        <div className="flex items-center gap-2 flex-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">L2</span>
                            <button
                                onClick={() => handleBreadcrumbClick(path.l2!, 2)}
                                className="text-xs font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left flex-1"
                            >
                                {path.l2}
                            </button>
                        </div>
                    ) : path.l1 ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Select L2 aspect</span>
                    ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                    )}
                    {path.l2 && path.l3 && (
                        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </div>

                {/* L4 breadcrumb - shows L3 selection with L3 label */}
                {path.l3 && (
                    <div className="flex-shrink-0 px-4 py-2.5 flex items-center" style={{ width: COLUMN_WIDTH }}>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">L3</span>
                            <button
                                onClick={() => handleBreadcrumbClick(path.l3!, 3)}
                                className="text-xs font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left"
                            >
                                {path.l3}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Columns grid - minimal style with fixed wide columns */}
            <div
                ref={scrollContainerRef}
                className="flex-grow flex bg-white dark:bg-gray-900 overflow-x-auto scroll-smooth"
                onScroll={handleScroll}
            >
                {/* Column 1 - L1 Aspects only */}
                <div className="flex-shrink-0 p-4 overflow-y-auto border-r border-gray-100 dark:border-gray-800" style={{ width: COLUMN_WIDTH }}>
                    <MindMapColumn
                        level={1}
                        aspects={l1Items}
                        themes={[]} // No themes in column 1
                        selectedAspectId={path.l1}
                        selectedThemeId={path.theme}
                        focusedAspectId={focusedItem?.level === 1 ? focusedItem.aspectName : undefined}
                        onSelectAspect={handleSelectAspect}
                        onSelectTheme={handleSelectTheme}
                        expandedThemes={expandedAspectThemeLists}
                        onToggleThemes={toggleAspectThemeList}
                        isDisabled={false}
                        allThemes={allThemes}
                        relationships={relationships}
                        allAspects={allAspects}
                        globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                        columnTitle=""
                        orgSlug={org_slug} // Pass org slug for L0 display
                    />
                </div>

                {/* Column 2 - L1 Themes + L2 Aspects */}
                <div className="flex-shrink-0 p-4 overflow-y-auto border-r border-gray-100 dark:border-gray-800" style={{ width: COLUMN_WIDTH }}>
                    <MindMapColumn
                        level={2}
                        aspects={l2Items}
                        themes={l1Themes} // L1 themes show here
                        selectedAspectId={path.l2}
                        selectedThemeId={path.theme}
                        focusedAspectId={focusedItem?.level === 2 ? focusedItem.aspectName : undefined}
                        onSelectAspect={handleSelectAspect}
                        onSelectTheme={handleSelectTheme}
                        expandedThemes={expandedAspectThemeLists}
                        onToggleThemes={toggleAspectThemeList}
                        isDisabled={!path.l1}
                        allThemes={allThemes}
                        relationships={relationships}
                        allAspects={allAspects}
                        globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                        columnTitle=""
                        parentAspectName={path.l1} // Pass parent aspect name for theme section label
                    />
                </div>

                {/* Column 3 - L2 Themes + L3 Aspects */}
                <div className="flex-shrink-0 p-4 overflow-y-auto border-r border-gray-100 dark:border-gray-800" style={{ width: COLUMN_WIDTH }}>
                    <MindMapColumn
                        level={3}
                        aspects={l3Items}
                        themes={l2Themes} // L2 themes show here
                        selectedAspectId={path.l3}
                        selectedThemeId={path.theme}
                        focusedAspectId={focusedItem?.level === 3 ? focusedItem.aspectName : undefined}
                        onSelectAspect={handleSelectAspect}
                        onSelectTheme={handleSelectTheme}
                        expandedThemes={expandedAspectThemeLists}
                        onToggleThemes={toggleAspectThemeList}
                        isDisabled={!path.l2}
                        allThemes={allThemes}
                        relationships={relationships}
                        allAspects={allAspects}
                        globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                        columnTitle=""
                        parentAspectName={path.l2} // Pass parent aspect name for theme section label
                    />
                </div>

                {/* Column 4 - L3 Themes only */}
                {path.l3 && ( // Only show column 4 if L3 is selected
                    <div className="flex-shrink-0 p-4 overflow-y-auto" style={{ width: COLUMN_WIDTH }}>
                        <MindMapColumn
                            level={4}
                            aspects={[]}
                            themes={l3Themes} // L3 themes show here
                            selectedAspectId={undefined}
                            selectedThemeId={path.theme}
                            focusedAspectId={undefined}
                            onSelectAspect={handleSelectAspect}
                            onSelectTheme={handleSelectTheme}
                            expandedThemes={expandedAspectThemeLists}
                            onToggleThemes={toggleAspectThemeList}
                            isDisabled={!path.l3}
                            allThemes={allThemes}
                            relationships={relationships}
                            allAspects={allAspects}
                            globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                            columnTitle=""
                            parentAspectName={path.l3} // Pass parent aspect name for theme section label
                        />
                    </div>
                )}
            </div>

            {/* Theme details drawer */}
            {selectedTheme && (
                <Portal>
                    <DetailsDrawer
                        theme={selectedTheme}
                        selectedPath={path}
                        onClose={() => setTheme(undefined)}
                        allThemes={allThemes}
                        allAspects={allAspects}
                    />
                </Portal>
            )}
        </div>
    );
};

export default TaxonomyExplorer; 